import { useEffect, useRef, useCallback } from 'react';

interface UseAutoLogoutOptions {
  isAuthenticated: boolean;
  logout: () => Promise<void> | void;
  timeoutMinutes?: number;
}

/** Throttle interval for resetting the timer on activity (ms) */
const ACTIVITY_THROTTLE_MS = 1000;

/**
 * Custom hook for auto logout on inactivity only.
 * - Logs out after no activity (mouse, keyboard, click, scroll) for VITE_AUTO_LOGOUT_TIME minutes (default 60).
 * - Does NOT call logout on page unload (beforeunload/pagehide): that would also run on refresh and
 *   cannot be reliably distinguished from tab/browser close, so we skip it to avoid logging out on refresh.
 */
export const useAutoLogout = ({
  isAuthenticated,
  logout,
  timeoutMinutes,
}: UseAutoLogoutOptions) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(0);

  const getTimeoutMinutes = useCallback((): number => {
    const envTimeout = import.meta.env.VITE_AUTO_LOGOUT_TIME;
    return timeoutMinutes ?? (envTimeout ? parseInt(String(envTimeout), 10) : 60);
  }, [timeoutMinutes]);

  const scheduleLogout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const minutes = getTimeoutMinutes();
    const timeoutMs = minutes * 60 * 1000;
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      const result = logout();
      if (result instanceof Promise) {
        result.catch((err: unknown) => console.error('Auto-logout error:', err));
      }
    }, timeoutMs);
  }, [logout, getTimeoutMinutes]);

  const resetInactivityTimer = useCallback(() => {
    const now = Date.now();
    if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
    lastActivityRef.current = now;
    scheduleLogout();
  }, [scheduleLogout]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    lastActivityRef.current = Date.now();
    scheduleLogout();

    const events = ['mousemove', 'keydown', 'click', 'scroll'] as const;
    const options = { passive: true, capture: true };

    events.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer, options);
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetInactivityTimer, options);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isAuthenticated, resetInactivityTimer, scheduleLogout]);
};
